import {
  createKeeperDraftSoundController,
  type KeeperDraftSoundController,
} from "./draft-sounds";

export interface KeeperDraftSoundSnapshot {
  status: "lobby" | "active" | "paused" | "completed";
  currentTeamId: string | null;
  currentTurnOpenedAt?: number | null;
  picks: readonly { id: string; pickNumber: number }[];
}

export interface KeeperDraftSoundEvents {
  selectionMade: boolean;
  onClock: boolean;
}

function turnIdentity(snapshot: KeeperDraftSoundSnapshot): string {
  return [
    snapshot.status,
    snapshot.currentTeamId ?? "",
    snapshot.currentTurnOpenedAt ?? "",
    snapshot.picks.length,
  ].join(":");
}

/**
 * Derives sound transitions from authoritative room snapshots. Initial room
 * hydration never replays an old selection, while a manager who enters or
 * reconnects during their active turn still receives the on-clock chime.
 */
export function keeperDraftSoundEvents(
  previous: KeeperDraftSoundSnapshot | null,
  next: KeeperDraftSoundSnapshot,
  viewerTeamId: string | null,
): KeeperDraftSoundEvents {
  const selectionMade = Boolean(
    previous
    && next.picks.length > previous.picks.length,
  );
  const onClock = Boolean(
    viewerTeamId
    && next.status === "active"
    && next.currentTeamId === viewerTeamId
    && (!previous || turnIdentity(previous) !== turnIdentity(next)),
  );
  return { selectionMade, onClock };
}

export interface KeeperDraftSoundSession {
  prime(): void;
  update(snapshot: KeeperDraftSoundSnapshot): KeeperDraftSoundEvents;
  reset(): void;
  dispose(): void;
}

export function createKeeperDraftSoundSession(
  viewerTeamId: string | null,
  controller: KeeperDraftSoundController = createKeeperDraftSoundController(),
): KeeperDraftSoundSession {
  let previous: KeeperDraftSoundSnapshot | null = null;
  let primed = false;
  let pendingSelectionMade = false;
  let pendingOnClock = false;

  return {
    prime() {
      controller.prime();
      if (primed) return;
      primed = true;
      if (pendingSelectionMade) controller.playSelectionMade();
      if (pendingOnClock) controller.playOnClock();
      pendingSelectionMade = false;
      pendingOnClock = false;
    },
    update(snapshot) {
      const events = keeperDraftSoundEvents(previous, snapshot, viewerTeamId);
      previous = snapshot;
      if (primed) {
        if (events.selectionMade) controller.playSelectionMade();
        if (events.onClock) controller.playOnClock();
      } else {
        pendingSelectionMade ||= events.selectionMade;
        pendingOnClock ||= events.onClock;
        // Do not replay a turn that ended before the browser received the
        // gesture needed to unlock Web Audio. Duplicate snapshots for the
        // still-active viewer turn leave the pending chime intact.
        if (
          snapshot.status !== "active"
          || !viewerTeamId
          || snapshot.currentTeamId !== viewerTeamId
        ) {
          pendingOnClock = false;
        }
      }
      return events;
    },
    reset() {
      previous = null;
      pendingSelectionMade = false;
      pendingOnClock = false;
    },
    dispose() {
      previous = null;
      pendingSelectionMade = false;
      pendingOnClock = false;
      controller.dispose();
    },
  };
}
